import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ProgramSidebarProvider } from '../contexts/ProgramSidebarContext';
import { PaidPassGateProvider, usePaidPassGate } from '../contexts/PaidPassGateContext';
import { loadGradesCache } from '../services/gradeTrackService';
import {
  loadClassScheduleSettingsCache,
  type ClassScheduleRow,
  type ClassScheduleSettings,
  type WeekdayKey,
} from '../services/sectionLayoutService';
import { isWeekdayTimeKey, resolveClassScheduleRowsForWeekday } from '../utils/classScheduleWeekdayTimes';
import type { AnnualWeekdayKey, Grade } from '../types/domain';
import { toISODate } from '../utils/date';
import { dateToWeekdayKey, mondayIsoForDateInLocal } from '../utils/dashboardDateContext';
import { annualSlotHasTimetableProgramAssignment } from '../utils/annualProgressSlotSource';
import type { DashboardAnnualDaySlot } from '../utils/dashboardAnnualDaySlots';
import { usePresentationLessonDaySlots } from '../hooks/usePresentationLessonDaySlots';
import ClassLessonAlarmPopupRelay from '../components/classAlarm/ClassLessonAlarmPopupRelay';
import { openDashboardAnnualSlotPresentation } from '../utils/openDashboardAnnualSlotPresentation';
import MainPresentationToolbar, {
  type MainPresentationBoardCanvasMode,
  type MainPresentationViewMode,
} from '../components/presentation/MainPresentationToolbar';
import EphemeralPresentationWhiteboard from '../components/presentation/EphemeralPresentationWhiteboard';
import type { WebcamDrawStroke } from '../components/program/ProgramSlideWebcamDrawOverlay';
import AssignmentManagementCardPanel from '../components/assignmentPoints/AssignmentManagementCardPanel';
import PointItemNotePage from './PointItemNotePage';
import AutoStatusToast from '../components/common/AutoStatusToast';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import { TutorialProvider } from '../contexts/TutorialContext';
import TutorialOverlayHost from '../components/tutorial/TutorialOverlayHost';
import PresentationTutorialBootstrap from '../components/tutorial/PresentationTutorialBootstrap';
import CachedAppImage from '../components/common/CachedAppImage';
import { SevenSegmentClock } from '../components/common/SevenSegmentDisplay';
import { requestAssignmentPointsNoteFromMainTab } from '../utils/assignmentPointsNoteTabBridge';
import { applyAppDocumentTitle } from '../utils/appDocumentTitle';
import { requestPointItemNoteFromMainTab } from '../utils/pointItemNoteTabBridge';
import { loadNoteSelectedGroupId } from '../utils/noteSelectedGroupPreference';
import { syncSchoolCachesWithQuery } from '../query/syncSchoolCachesWithQuery';
import { useRemoteReceiver } from '../hooks/useRemoteReceiver';
import RemoteBacklogNavButton from '../components/topbar/RemoteBacklogNavButton';
import { supabase } from '../supabaseClient';
import {
  getRemoteDeviceId,
  readAllowRemoteUntil,
  writeAllowRemoteControl,
} from '../utils/presentationRemoteIdentity';
const EMPTY_BOARD_DRAW_STROKES: WebcamDrawStroke[] = [];

function parseHHMMToMinutes(raw: string): number | null {
  const m = /^(\d{1,2})\s*:\s*(\d{2})$/.exec((raw ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

interface ScheduleSlot {
  kind: ClassScheduleRow['kind'];
  name: string;
  startMin: number;
  endMin: number;
}

/** 워크스페이스 기초 시간표에서 시각이 있는 행만 시작시각 순으로 정리 */
function buildSlots(
  settings: ClassScheduleSettings | null,
  weekdayKey: WeekdayKey | null
): ScheduleSlot[] {
  if (!settings) return [];
  const sectionId = settings.sections.some((s) => s.id === 'base')
    ? 'base'
    : settings.sections[0]?.id;
  if (!sectionId) return [];
  const rows = resolveClassScheduleRowsForWeekday(settings, sectionId, weekdayKey);
  const slots: ScheduleSlot[] = [];
  for (const row of rows) {
    const startMin = parseHHMMToMinutes(row.startTime);
    let endMin = parseHHMMToMinutes(row.endTime);
    if (startMin == null) continue;
    if (endMin == null) {
      const dur = Number((row.durationMinutes ?? '').trim());
      if (Number.isFinite(dur) && dur > 0) endMin = startMin + dur;
    }
    if (endMin == null || endMin <= startMin) continue;
    slots.push({ kind: row.kind, name: (row.name ?? '').trim(), startMin, endMin });
  }
  return slots.sort((a, b) => a.startMin - b.startMin);
}

/**
 * 현재 시각이 속한 교시/쉬는 시간/방과후 라벨.
 * 쉬는 시간·수업 전처럼 다음 교시를 기다리는 상태에서는 `nextStartLabel`("N교시 시작 N분전")을 함께 반환.
 * 분 단위 카운트다운(nowMin이 분 단위라 매 분 변경).
 */
function formatMinutesHHMM(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function resolveNowInfo(
  slots: ScheduleSlot[],
  nowMin: number
): {
  label: string;
  nextStartLabel: string | null;
  /** 현재시각 끔 모드용: "N교시 시작시각 HH:MM" */
  nextStartTimeLabel: string | null;
  /** 수업 중인 교시 이름(현재시각 끔 모드에서 편제명과 함께 표시) */
  insideClassName: string | null;
} {
  if (slots.length === 0) {
    return { label: '', nextStartLabel: null, nextStartTimeLabel: null, insideClassName: null };
  }

  const inside = slots.find((s) => nowMin >= s.startMin && nowMin < s.endMin);
  if (inside) {
    if (inside.kind === 'after_school') {
      return { label: '방과후', nextStartLabel: null, nextStartTimeLabel: null, insideClassName: null };
    }
    return {
      label: inside.name || '수업 중',
      nextStartLabel: null,
      nextStartTimeLabel: null,
      insideClassName: inside.kind === 'class' ? inside.name || null : null,
    };
  }

  const classSlots = slots.filter((s) => s.kind === 'class');
  const lastEndedClass = [...classSlots].reverse().find((s) => s.endMin <= nowMin) ?? null;
  const nextClass = classSlots.find((s) => s.startMin > nowMin) ?? null;

  const nextStartLabel = nextClass
    ? `${nextClass.name} 시작 ${Math.max(0, nextClass.startMin - nowMin)}분전`
    : null;
  const nextStartTimeLabel = nextClass
    ? `${nextClass.name} 시작시각 ${formatMinutesHHMM(nextClass.startMin)}`
    : null;

  if (lastEndedClass && nextClass) {
    return {
      label: `${lastEndedClass.name} 쉬는 시간`,
      nextStartLabel,
      nextStartTimeLabel,
      insideClassName: null,
    };
  }
  if (lastEndedClass && !nextClass) {
    return { label: '방과후', nextStartLabel: null, nextStartTimeLabel: null, insideClassName: null };
  }
  return { label: '수업 전', nextStartLabel, nextStartTimeLabel, insideClassName: null };
}

const MAIN_PRES_SHOW_LESSON_DETAIL_LS = 'cp-main-pres-show-lesson-detail-v1';
const MAIN_PRES_SHOW_CLOCK_LS = 'cp-main-pres-show-clock-v1';

function readMainPresBoolOption(key: string): boolean {
  try {
    return window.localStorage.getItem(key) !== '0';
  } catch {
    return true;
  }
}

function writeMainPresBoolOption(key: string, value: boolean): void {
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/** 연간: 시간표 칸이 비어 있으면 진도를 붙이지 않는 것과 같게, ‘수업 없음’ */
function isSlotNoLesson(slot: DashboardAnnualDaySlot): boolean {
  if (slot.timetableSlotKind === 'out_of_term' || slot.timetableSlotKind === 'holiday') return true;
  return !annualSlotHasTimetableProgramAssignment(slot.timetableText, slot.timetableColorHex);
}





/** 상단 오늘 시간표 블럭 — 대시보드 데일리와 같은 학기 캐시를 공유(캐시 hit 시 네트워크 없음) */
const PresentationTimetableRow: React.FC<{
  workspaceId: string;
  gradeId: string;
  grades: Grade[];
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey | null;
  dateIso: string;
  activePeriodLabel: string | null;
  showLessonDetail: boolean;
  onPeriodPyeonjeMapChange?: (map: Record<string, string>) => void;
}> = ({
  workspaceId,
  gradeId,
  grades,
  mondayIso,
  weekdayKey,
  dateIso,
  activePeriodLabel,
  showLessonDetail,
  onPeriodPyeonjeMapChange,
}) => {
  const { daySlots, loading, academicYear, activeTermNumber, cacheMiss } = usePresentationLessonDaySlots({
    workspaceId,
    gradeId,
    grades,
    mondayIso,
    weekdayKey,
    dateIso,
    enabled: !!workspaceId && !!gradeId && weekdayKey != null,
  });
  const error: string | null = null;

  useEffect(() => {
    if (!onPeriodPyeonjeMapChange) return;
    const map: Record<string, string> = {};
    for (const slot of daySlots) {
      const period = (slot.periodLabel ?? '').trim();
      if (!period || isSlotNoLesson(slot)) continue;
      const pyeonje = (slot.templateLabel ?? '').trim() || (slot.timetableText ?? '').trim();
      if (pyeonje) map[period] = pyeonje;
    }
    onPeriodPyeonjeMapChange(map);
  }, [daySlots, onPeriodPyeonjeMapChange]);

  const { toastState, showInfo } = useAutoStatusToast(5000);
  const cacheMissToastShownRef = useRef(false);

  useEffect(() => {
    cacheMissToastShownRef.current = false;
  }, [dateIso, gradeId]);

  useEffect(() => {
    if (!cacheMiss || loading) return;
    if (cacheMissToastShownRef.current) return;
    cacheMissToastShownRef.current = true;
    showInfo('이 날짜의 교시 블럭 데이터를 찾지 못했습니다. 시간표·학기 설정을 확인해 주세요');
  }, [cacheMiss, loading, showInfo, dateIso, gradeId]);

  useEffect(() => {
    if (loading || daySlots.length === 0) return;
    // 캐시 hit로 네트워크 없이 채워진 경우를 TopNav에 표시
    if (!error) recordDevLocalStorageProgramCacheUse('메인프레젠·대시보드캐시');
  }, [loading, daySlots.length, error, dateIso, gradeId]);

  /**
   * 연간진도표 '프레젠테이션 열기'와 동일한 방식으로 연다(원격 버튼).
   * 메인 프레젠에서는 presence·owner를 LS 캐시만으로 고르고 REST는 치지 않는다.
   */
  const openSlotPresentation = (slot: DashboardAnnualDaySlot) => {
    openDashboardAnnualSlotPresentation({
      slot,
      workspaceId,
      gradeId,
      academicYear,
      termNumber: activeTermNumber ?? slot.termNumber,
      sameWindow: true,
    });
  };

  /** 방과후만 제외, 교시는 빈 교시 포함 모두 표시 */
  const periodSlots = daySlots.filter((slot) => {
    const label = (slot.periodLabel ?? '').trim();
    return label !== '' && !label.includes('방과후');
  });

  if (periodSlots.length === 0) {
    /* daySlots 자체가 비면 캐시 미워밍 가능성이 큼(방과후만 있는 날은 daySlots는 있음) */
    const needCacheWarm = cacheMiss || grades.length === 0;
    return (
      <>
        <div className="cp-dash-presentation-timetable-row cp-dash-presentation-timetable-row--empty">
          <span className="cp-dash-presentation-timetable-empty">
            {weekdayKey == null
              ? '주말 · 이 날짜의 시간표가 없습니다'
              : loading
                ? '시간표를 불러오는 중…'
                : error
                  ? `시간표를 불러오지 못했습니다: ${error}`
                  : needCacheWarm
                    ? '이 날짜의 시간표 데이터를 찾지 못했습니다'
                    : '이 날짜에 표시할 교시가 없습니다'}
          </span>
        </div>
        <AutoStatusToast state={toastState} className="cp-toast-auto-over-modal" />
      </>
    );
  }

  /** 한 블럭이라도 단원·학습내용이 있으면 수업 블럭 전체를 같은 세로 배치로(교시·편제 줄 높이 통일) */
  const rowHasDetail =
    showLessonDetail &&
    periodSlots.some((slot) => {
      if (isSlotNoLesson(slot) || slot.timetableSlotKind === 'holiday') return false;
      return Boolean(
        (slot.display?.progressUnit ?? '').trim() || (slot.display?.progressLearning ?? '').trim()
      );
    });

  return (
    <>
    <div className="cp-dash-presentation-timetable-row" role="list">
      {periodSlots.map((slot) => {
        const noLesson = isSlotNoLesson(slot);
        const period = (slot.periodLabel ?? '').trim();
        const pyeonje = (slot.templateLabel ?? '').trim() || (slot.timetableText ?? '').trim();
        const hex = (slot.timetableColorHex ?? '').trim();
        const isActive = activePeriodLabel != null && period === activePeriodLabel;
        const clickable = !noLesson;
        const isCalendarOffDay = slot.timetableSlotKind === 'holiday';
        const isHoliday = isCalendarOffDay || /공휴일|휴업일/.test(pyeonje);
        const holidayLabel = isCalendarOffDay
          ? (slot.timetableText ?? '').trim() ||
            (slot.calendarOffDayKind === 'closure'
              ? '휴업일'
              : slot.calendarOffDayKind === 'vacation'
                ? '방학'
                : '공휴일')
          : pyeonje || '공휴일';
        const unit = (slot.display?.progressUnit ?? '').trim();
        const learning = (slot.display?.progressLearning ?? '').trim();
        const hasDetail = rowHasDetail && !noLesson && !isHoliday;

        return (
          <div
            key={slot.slotIndex}
            className={[
              'cp-dash-presentation-block',
              noLesson ? 'is-no-lesson' : '',
              isHoliday ? 'is-holiday' : '',
              isActive ? 'is-active' : '',
              clickable ? 'is-clickable' : '',
              hasDetail ? 'has-detail' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={
              /^#[0-9A-Fa-f]{6}$/.test(hex)
                ? ({ '--cp-dash-slot-color': hex } as React.CSSProperties)
                : undefined
            }
            role={clickable ? 'button' : 'listitem'}
            tabIndex={clickable ? 0 : undefined}
            title={clickable ? '이 수업의 프레젠테이션 열기' : undefined}
            onClick={clickable ? () => openSlotPresentation(slot) : undefined}
            onKeyDown={
              clickable
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openSlotPresentation(slot);
                    }
                  }
                : undefined
            }
          >
            <div className="cp-dash-presentation-block-head">
            <div className="cp-dash-presentation-block-period">{period}</div>
            {isHoliday ? (
              <span className="cp-dash-presentation-block-empty-text is-holiday">{holidayLabel}</span>
            ) : noLesson ? (
              <span className="cp-dash-presentation-block-empty-text">수업 없음</span>
            ) : (
              <div className="cp-dash-presentation-block-pyeonje">
                <span
                  className="cp-dash-presentation-block-dot"
                  style={hex ? { backgroundColor: hex } : undefined}
                  aria-hidden
                />
                <span className="cp-dash-presentation-block-pyeonje-text">{pyeonje}</span>
              </div>
            )}
            </div>
            {hasDetail ? (
              <div className="cp-dash-presentation-block-detail">
                <span className="cp-dash-presentation-block-unit" title={unit || undefined}>
                  {unit || '\u00a0'}
                </span>
                <span className="cp-dash-presentation-block-learning" title={learning || undefined}>
                  {learning || '\u00a0'}
                </span>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
    <AutoStatusToast state={toastState} className="cp-toast-auto-over-modal" />
    </>
  );
};

const DashboardPresentationPage: React.FC = () => (
  <PaidPassGateProvider>
    <DashboardPresentationPageGate />
  </PaidPassGateProvider>
);

/**
 * 메인 프레젠 자체는 이용권별 권한 main_presentation(기본: 모든 이용권 사용)으로 연다.
 * 이용권이 확정(ready)되고 잠긴 경우에만 막는다 — 확인 중(pending/error)에는 그대로 연다.
 * 서버 비용이 드는 기능(과제 카드·포인트 = DB 읽기/쓰기, 다른 기기 원격 = Realtime)은
 * 내부에서 canUseFeature(...) 로 따로 잠근다. 이용권 확인 중(pending/error)에도 잠금 상태로 시작.
 */
const DashboardPresentationPageGate: React.FC = () => {
  const { isFeatureLocked, openPaidPassRequiredModal } = usePaidPassGate();
  const locked = isFeatureLocked('main_presentation');
  useEffect(() => {
    if (locked) openPaidPassRequiredModal();
  }, [locked, openPaidPassRequiredModal]);
  if (!locked) return <DashboardPresentationPageInner />;
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return (
    <div
      role="alert"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: 24,
        textAlign: 'center',
        color: 'var(--color-text-primary)',
        background: 'var(--color-bg, var(--color-surface))',
      }}
    >
      <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>메인 프레젠테이션은 현재 이용권에서 사용할 수 없어요.</p>
      <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-secondary)' }}>
        「이용권 구매 및 관리」에서 이용권을 확인해 주세요.
      </p>
      <a className="cp-btn-save-all" href={`${base}/subscription`}>
        이용권 보기
      </a>
    </div>
  );
};

const DashboardPresentationPageInner: React.FC = () => {
  const { workspaceId, gradeParam, dateParam } = useMemo(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      return {
        workspaceId: q.get('ws')?.trim() ?? '',
        gradeParam: q.get('grade')?.trim() ?? '',
        dateParam: q.get('date')?.trim() ?? '',
      };
    } catch {
      return { workspaceId: '', gradeParam: '', dateParam: '' };
    }
  }, []);

  /** 유료 전용(서버 비용) 기능 사용 가능 여부 — 이용권 확인 전에는 false */
  const { canUseFeature, openPaidPassRequiredModal } = usePaidPassGate();
  const canUseRemote = canUseFeature('remote_control');
  const canUseAssignment = canUseFeature('assignment_note');
  const canUsePointItem = canUseFeature('point_item_note');

  const [grades, setGrades] = useState<Grade[]>(() =>
    workspaceId ? loadGradesCache(workspaceId) ?? [] : []
  );
  const [settings, setSettings] = useState<ClassScheduleSettings | null>(() =>
    workspaceId ? loadClassScheduleSettingsCache(workspaceId) : null
  );
  const [now, setNow] = useState(() => new Date());
  /** 시간표 블럭 표시 기준 날짜. URL ?date= 가 있으면 그걸 초기값으로(메인 탭과 맞춤) */
  const [viewDate, setViewDate] = useState(() => {
    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
      const [y, m, d] = dateParam.split('-').map(Number);
      const parsed = new Date(y, m - 1, d);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  });

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  /* 메인 프레젠: grades·시간표설정은 LS만 (REST 차단) */
  useEffect(() => {
    if (!workspaceId) return;
    setGrades(loadGradesCache(workspaceId) ?? []);
    setSettings(loadClassScheduleSettingsCache(workspaceId));
  }, [workspaceId]);

  /* 페이지 로드(새로고침)당 1회 파트별 revision 비교 — 바뀐 파트의 쿼리만 다시 받음 */
  useEffect(() => {
    if (!workspaceId) return undefined;
    let cancelled = false;
    void syncSchoolCachesWithQuery(workspaceId)
      .then((isFresh) => {
        if (cancelled || isFresh) return;
        const nextGrades = loadGradesCache(workspaceId);
        if (nextGrades && nextGrades.length > 0) setGrades(nextGrades);
        const nextSettings = loadClassScheduleSettingsCache(workspaceId);
        if (nextSettings) setSettings(nextSettings);
      })
      .catch(() => {
        /* 오프라인 등 — 캐시로 계속 표시 */
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const gradeId = useMemo(() => {
    if (gradeParam && grades.some((g) => g.id === gradeParam)) return gradeParam;
    return grades[0]?.id ?? gradeParam;
  }, [gradeParam, grades]);

  const viewDateIso = toISODate(viewDate);
  const weekdayKey = useMemo(() => dateToWeekdayKey(viewDate), [viewDate]);
  const mondayIso = useMemo(() => mondayIsoForDateInLocal(viewDate), [viewDate]);

  const shiftViewDate = (deltaDays: number) => {
    setViewDate((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + deltaDays);
      return d;
    });
  };

  const isViewingToday = viewDateIso === toISODate(now);
  const viewDateLabel = useMemo(() => {
    const m = viewDate.getMonth() + 1;
    const day = viewDate.getDate();
    const dow = (['일', '월', '화', '수', '목', '금', '토'] as const)[viewDate.getDay()];
    return `${m}월 ${day}일 (${dow})${isViewingToday ? ' 오늘' : ''}`;
  }, [viewDate, isViewingToday]);

  const weekdayForSchedule = isWeekdayTimeKey(weekdayKey) ? weekdayKey : null;
  const scheduleSlots = useMemo(
    () => buildSlots(settings, weekdayForSchedule),
    [settings, weekdayForSchedule]
  );
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const { label: nowLabel, nextStartLabel, nextStartTimeLabel, insideClassName } = resolveNowInfo(
    scheduleSlots,
    nowMin
  );

  const [showLessonDetail, setShowLessonDetail] = useState(() =>
    readMainPresBoolOption(MAIN_PRES_SHOW_LESSON_DETAIL_LS)
  );
  const [showClock, setShowClock] = useState(() => readMainPresBoolOption(MAIN_PRES_SHOW_CLOCK_LS));
  const [optionsOpen, setOptionsOpen] = useState(false);
  const optionsWrapRef = useRef<HTMLDivElement | null>(null);
  const [periodPyeonjeMap, setPeriodPyeonjeMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!optionsOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (optionsWrapRef.current && !optionsWrapRef.current.contains(e.target as Node)) {
        setOptionsOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOptionsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [optionsOpen]);

  const toggleShowLessonDetail = () => {
    const next = !showLessonDetail;
    setShowLessonDetail(next);
    writeMainPresBoolOption(MAIN_PRES_SHOW_LESSON_DETAIL_LS, next);
  };
  /* —— 원격 제어 (유료, 계정+기기별, 기본 OFF, 켜면 1시간 뒤 자동 OFF) —— */
  const [remoteUserId, setRemoteUserId] = useState<string | null>(null);
  const [allowUntil, setAllowUntil] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    void supabase.auth
      .getSession()
      .then(({ data }) => {
        const uid = data.session?.user?.id ?? null;
        if (!alive || !uid) return;
        setRemoteUserId(uid);
        setAllowUntil(readAllowRemoteUntil(uid, getRemoteDeviceId()));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  useEffect(() => {
    if (!remoteUserId || allowUntil == null) return undefined;
    const left = allowUntil - Date.now();
    if (left <= 0) {
      writeAllowRemoteControl(remoteUserId, getRemoteDeviceId(), false);
      setAllowUntil(null);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      writeAllowRemoteControl(remoteUserId, getRemoteDeviceId(), false);
      setAllowUntil(null);
    }, left);
    return () => window.clearTimeout(timer);
  }, [allowUntil, remoteUserId]);
  const allowRemoteControl = allowUntil != null && allowUntil > Date.now();
  const toggleAllowRemoteControl = () => {
    if (!canUseRemote) {
      openPaidPassRequiredModal();
      return;
    }
    if (!remoteUserId) return;
    const next = !allowRemoteControl;
    setAllowUntil(writeAllowRemoteControl(remoteUserId, getRemoteDeviceId(), next));
  };
  /** 이용권 만료 시 자동으로 끊김 (저장값은 유지) */
  const remoteControlActive = allowRemoteControl && canUseRemote;

  const toggleShowClock = () => {
    const next = !showClock;
    setShowClock(next);
    writeMainPresBoolOption(MAIN_PRES_SHOW_CLOCK_LS, next);
  };

  /** 현재 시각이 포함되거나(수업 중) 앞으로 가장 가까운 교시 라벨 */
  const activePeriodLabel = useMemo(() => {
    const classSlots = scheduleSlots.filter((s) => s.kind === 'class');
    const inside = classSlots.find((s) => nowMin >= s.startMin && nowMin < s.endMin);
    if (inside) return inside.name || null;
    const next = classSlots.find((s) => s.startMin > nowMin);
    return next ? next.name || null : null;
  }, [scheduleSlots, nowMin]);
  const activityLabel = useMemo(() => {
    if (!nowLabel) return '';
    if (insideClassName) {
      const pyeonje = isViewingToday ? (periodPyeonjeMap[insideClassName] ?? '').trim() : '';
      return pyeonje ? `${insideClassName} ${pyeonje}` : insideClassName;
    }
    return nowLabel;
  }, [nowLabel, insideClassName, isViewingToday, periodPyeonjeMap]);
  const clock = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

  const [viewMode, setViewMode] = useState<MainPresentationViewMode>('guide');
  const [boardCanvasMode, setBoardCanvasMode] =
    useState<MainPresentationBoardCanvasMode>('edit');

  /** 칠판 판서: 페이지별 임시 보관(메모리 — 탭을 닫으면 사라짐) */
  const [boardPageIndex, setBoardPageIndex] = useState(0);
  const [boardDrawStrokesByPage, setBoardDrawStrokesByPage] = useState<Record<number, WebcamDrawStroke[]>>({});
  const boardDrawStrokes = boardDrawStrokesByPage[boardPageIndex] ?? EMPTY_BOARD_DRAW_STROKES;
  const setBoardDrawStrokesForPage = useCallback(
    (next: WebcamDrawStroke[]) => {
      setBoardDrawStrokesByPage((prev) => ({ ...prev, [boardPageIndex]: next }));
    },
    [boardPageIndex]
  );
  const handleBoardPageDeleted = useCallback((removedPageIndex: number) => {
    setBoardDrawStrokesByPage((prev) => {
      const next: Record<number, WebcamDrawStroke[]> = {};
      Object.entries(prev).forEach(([key, strokes]) => {
        const idx = Number(key);
        if (!Number.isFinite(idx) || idx === removedPageIndex) return;
        next[idx > removedPageIndex ? idx - 1 : idx] = strokes;
      });
      return next;
    });
  }, []);

  useEffect(() => {
    applyAppDocumentTitle();
  }, []);

  /* 진입 시·뷰 전환 시 메인 탭에 캐시 워밍 요청 (프레젠은 REST 없음) */
  useEffect(() => {
    if (!workspaceId || !gradeId) return;
    if (canUseAssignment) requestAssignmentPointsNoteFromMainTab({ workspaceId, gradeId });
    if (canUsePointItem) {
      requestPointItemNoteFromMainTab({
        workspaceId,
        gradeId,
        groupId: loadNoteSelectedGroupId('pointItem', workspaceId, gradeId) ?? undefined,
      });
    }
  }, [canUseAssignment, canUsePointItem, workspaceId, gradeId]);

  useEffect(() => {
    if (!workspaceId || !gradeId) return;
    if (viewMode === 'assignmentCard' && canUseAssignment) {
      requestAssignmentPointsNoteFromMainTab({ workspaceId, gradeId });
      return;
    }
    if (viewMode === 'pointItem' && canUsePointItem) {
      requestPointItemNoteFromMainTab({
        workspaceId,
        gradeId,
        groupId: loadNoteSelectedGroupId('pointItem', workspaceId, gradeId) ?? undefined,
      });
    }
  }, [canUseAssignment, canUsePointItem, viewMode, workspaceId, gradeId]);

  /** 과제 카드·포인트(DB 사용)는 유료 전용 — 프리는 안내 모달 */
  const handleViewModeChange = useCallback(
    (mode: MainPresentationViewMode) => {
      if ((mode === 'assignmentCard' && !canUseAssignment) || (mode === 'pointItem' && !canUsePointItem)) {
        openPaidPassRequiredModal();
        return;
      }
      setViewMode(mode);
    },
    [canUseAssignment, canUsePointItem, openPaidPassRequiredModal]
  );
  /* 이용권 만료 등으로 잠기면 유료 화면에서 빠져나옴 */
  useEffect(() => {
    setViewMode((cur) =>
      (cur === 'assignmentCard' && !canUseAssignment) || (cur === 'pointItem' && !canUsePointItem) ? 'guide' : cur
    );
  }, [canUseAssignment, canUsePointItem]);

  // 프레젠테이션 탭은 App 테마 부트스트랩을 건너뛰므로 LS에서 테마를 직접 반영
  useEffect(() => {
    const stored = window.localStorage.getItem('curriculum-theme');
    const next = stored === 'light' || stored === 'dark' ? stored : 'light';
    document.documentElement.dataset.theme = next;
  }, []);

  const guideFooterRef = useRef<HTMLDivElement | null>(null);

  /* 수업안내 하단 네비 실측 → 자막·웹캠 하단 맞춤용 CSS 변수 */
  useLayoutEffect(() => {
    if (viewMode !== 'guide') return undefined;
    const el = guideFooterRef.current;
    if (!el) return undefined;
    const page = el.closest('.cp-dash-presentation-page') as HTMLElement | null;
    const target = page ?? document.documentElement;
    const sync = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      target.style.setProperty('--cp-dash-board-footer-h', `${Math.max(h, 1)}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [viewMode]);

  /* 리모컨: Presence 등록 + 공지/질문/읽어주기/음악 수신 (세션 없음/실패 시 무동작) */
  const { overlays: remoteOverlays, bgm } = useRemoteReceiver({ remoteEnabled: remoteControlActive });

  return (
    <TutorialProvider workspaceId={workspaceId}>
    <div
      className={
        'cp-dash-presentation-page' +
        (viewMode === 'board' ? ' cp-dash-presentation-page--board' : '') +
        (viewMode === 'guide' ? ' cp-dash-presentation-page--guide' : '') +
        (viewMode === 'assignmentCard' ? ' cp-dash-presentation-page--assignment-card' : '') +
        (viewMode === 'pointItem' ? ' cp-dash-presentation-page--point-item' : '') +
        (showLessonDetail ? '' : ' cp-dash-presentation-page--detail-off') +
        (showClock ? '' : ' cp-dash-presentation-page--clock-off')
      }
      data-tutorial-pres="page"
    >
      <PresentationTutorialBootstrap />
      <MainPresentationToolbar
        bgm={bgm}
        workspaceId={workspaceId}
        gradeId={gradeId}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        paidTabsLocked={!canUseAssignment && !canUsePointItem}
        assignmentTabLocked={!canUseAssignment}
        pointTabLocked={!canUsePointItem}
        boardCanvasMode={boardCanvasMode}
        onBoardCanvasModeChange={setBoardCanvasMode}
        boardDrawStrokes={boardDrawStrokes}
        onBoardDrawStrokesChange={setBoardDrawStrokesForPage}
      />

      <div
        className="cp-dash-presentation-guide"
        hidden={viewMode !== 'guide'}
        aria-hidden={viewMode !== 'guide'}
        data-tutorial-pres="guide"
      >
        <div className="cp-dash-presentation-top">
          <ProgramSidebarProvider
            grades={grades}
            workspaceId={workspaceId}
            initialSidebarFocusGradeId={gradeId || null}
          >
            <PresentationTimetableRow
              workspaceId={workspaceId}
              gradeId={gradeId}
              grades={grades}
              mondayIso={mondayIso}
              weekdayKey={weekdayKey}
              dateIso={viewDateIso}
              activePeriodLabel={activePeriodLabel}
              showLessonDetail={showLessonDetail}
              onPeriodPyeonjeMapChange={setPeriodPyeonjeMap}
            />
          </ProgramSidebarProvider>
        </div>

        <div className="cp-dash-presentation-clock-area" data-tutorial-pres="clock">
          {/* 남은 메시지 아이콘 — 현재시각 표시 여부와 무관하게 항상 교시 글자 위 */}
          <div className="cp-remote-bl-clockslot">
            <RemoteBacklogNavButton variant="clock" />
          </div>
          {showClock ? (
            <>
              <div className="cp-dash-presentation-period" aria-live="polite">
                {nowLabel || '시간표 정보가 없습니다'}
              </div>
              <SevenSegmentClock
                time={clock}
                className="cp-dash-presentation-time"
                ariaLabel={`현재 시각 ${clock}`}
              />
              {nextStartLabel ? (
                <div className="cp-dash-presentation-next" aria-live="polite">
                  {nextStartLabel}
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="cp-dash-presentation-period cp-dash-presentation-period--activity" aria-live="polite">
                {activityLabel || '시간표 정보가 없습니다'}
              </div>
              {nextStartTimeLabel ? (
                <div className="cp-dash-presentation-next cp-dash-presentation-next--activity" aria-live="polite">
                  {nextStartTimeLabel}
                </div>
              ) : null}
            </>
          )}
        </div>

        <CachedAppImage
          file="character_1.webp"
          className="cp-dash-presentation-guide-character"
        />

        <div
          className="cp-program-slide-footer-nav cp-dash-presentation-guide-footer"
          ref={guideFooterRef}
        >
          <span className="cp-dash-presentation-guide-footer-side" aria-hidden />
          <div className="cp-program-slide-footer-nav-center">
            <div
              className="cp-dash-presentation-datenav"
              role="group"
              aria-label="시간표 날짜 이동"
              data-tutorial-pres="datenav"
            >
              <button
                type="button"
                className="cp-dash-presentation-datenav-btn"
                onClick={() => shiftViewDate(-1)}
                aria-label="이전 날짜"
              >
                &lt;
              </button>
              <button
                type="button"
                className={`cp-dash-presentation-datenav-label${isViewingToday ? ' is-today' : ''}`}
                onClick={() => setViewDate(new Date())}
                title="오늘로"
              >
                {viewDateLabel}
              </button>
              <button
                type="button"
                className="cp-dash-presentation-datenav-btn"
                onClick={() => shiftViewDate(1)}
                aria-label="다음 날짜"
              >
                &gt;
              </button>
            </div>
          </div>
          <div
            className="cp-dash-presentation-guide-footer-side cp-dash-presentation-options"
            ref={optionsWrapRef}
          >
            <button
              type="button"
              className={`cp-dash-presentation-remote-btn${remoteControlActive ? ' is-on' : ''}`}
              onClick={toggleAllowRemoteControl}
              aria-pressed={remoteControlActive}
              title={
                canUseRemote
                  ? remoteControlActive
                    ? '다른 기기 리모트에서 이 화면을 제어할 수 있습니다. 1시간 후 자동으로 꺼집니다.'
                    : '다른 기기 리모트에서 이 화면을 제어할 수 있게 합니다. 켜면 1시간 후 자동으로 꺼집니다.'
                  : '유료 플랜에서 다른 기기 제어를 사용할 수 있어요'
              }
            >
              원격 제어
              <span className="cp-dash-presentation-remote-state">{remoteControlActive ? 'ON' : 'OFF'}</span>
            </button>
            <button
              type="button"
              className={`cp-dash-presentation-options-btn${optionsOpen ? ' is-open' : ''}`}
              onClick={() => setOptionsOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={optionsOpen}
              title="표시 옵션"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M4 7h10M18 7h2M4 17h4M12 17h8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <circle cx="16" cy="7" r="2" stroke="currentColor" strokeWidth="2" />
                <circle cx="10" cy="17" r="2" stroke="currentColor" strokeWidth="2" />
              </svg>
              옵션
            </button>
            {optionsOpen ? (
              <div className="cp-dash-presentation-options-menu" role="menu" aria-label="표시 옵션">
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={showLessonDetail}
                  className="cp-dash-presentation-options-item"
                  onClick={toggleShowLessonDetail}
                >
                  <span>단원·학습내용 표시</span>
                  <span className={`cp-dash-presentation-options-switch${showLessonDetail ? ' is-on' : ''}`} aria-hidden />
                </button>
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={showClock}
                  className="cp-dash-presentation-options-item"
                  onClick={toggleShowClock}
                >
                  <span>현재 시각</span>
                  <span className={`cp-dash-presentation-options-switch${showClock ? ' is-on' : ''}`} aria-hidden />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* 칠판은 언마운트하지 않아 시간안내↔칠판 전환 시에도 임시 내용 유지(탭 닫으면 소멸) */}
      <div
        className="cp-dash-presentation-board-host"
        data-tutorial-pres="board-slide"
        hidden={viewMode !== 'board'}
        aria-hidden={viewMode !== 'board'}
      >
        <EphemeralPresentationWhiteboard
          canvasViewMode={boardCanvasMode === 'view'}
          onActivePageChange={setBoardPageIndex}
          onPageDeleted={handleBoardPageDeleted}
        />
      </div>

      {/* 칠판과 같이 언마운트하지 않음 — 재클릭 시 재로드/불러오는중 방지, 백그라운드 워밍 */}
      <div
        className="cp-dash-presentation-assignment-card-host"
        data-tutorial-pres="assignment-host"
        hidden={viewMode !== 'assignmentCard'}
        aria-hidden={viewMode !== 'assignmentCard'}
      >
        {!canUseAssignment ? null : workspaceId && gradeId ? (
          <AssignmentManagementCardPanel workspaceId={workspaceId} gradeId={gradeId} />
        ) : (
          <div className="cp-dash-presentation-assignment-card-empty">
            클래스를 선택하면 과제 카드를 사용할 수 있습니다.
          </div>
        )}
      </div>

      <div
        className="cp-dash-presentation-point-item-host"
        data-tutorial-pres="point-host"
        hidden={viewMode !== 'pointItem'}
        aria-hidden={viewMode !== 'pointItem'}
      >
        {!canUsePointItem ? null : workspaceId && gradeId ? (
          <PointItemNotePage
            workspaceId={workspaceId}
            currentGradeId={gradeId}
            grades={grades}
            variant="presentation"
            active={viewMode === 'pointItem'}
          />
        ) : (
          <div className="cp-dash-presentation-assignment-card-empty">
            클래스를 선택하면 포인트를 사용할 수 있습니다.
          </div>
        )}
      </div>

      {remoteOverlays}
      <ClassLessonAlarmPopupRelay workspaceId={workspaceId} />
      <TutorialOverlayHost />
    </div>
    </TutorialProvider>
  );
};

export default DashboardPresentationPage;
